import { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { config } from '@/config';
import { format, subDays } from 'date-fns';
import { Users, Building2, TrendingUp, MousePointerClick, Eye, BookOpen, Pencil, X, AlertTriangle, Trash2, Save, ChevronRight, Plus, Copy, RefreshCw, MapPin, Globe, CheckCircle2, Image as ImageIcon, Upload, Loader2 } from 'lucide-react';
import type { CloudinaryUploadWidgetOptions } from '@/types/cloudinary';
import { PlaybookTab } from '@/components/internal/PlaybookTab';

// ─── constants ────────────────────────────────────────────────────────────────

const PRIMARY   = '#2C5F2D';
const API       = config.apiUrl;
const STORE_KEY = 'internalToolToken';

type Granularity = 'hour' | 'day' | 'week' | 'month';

// ─── types ────────────────────────────────────────────────────────────────────

interface AdminStats {
  users:      { total: number; newThisWeek: number };
  businesses: { total: number; active: number; newThisWeek: number };
  leads:      { total: number; conversions: number; profileViews: number; bookingClicks: number; conversionRate: number };
  bookings:   { newThisWeek: number };
  dailyLeads: { date: string; leads: number; conversions: number }[];
  byCountry:  { country: string; businesses: number; users: number }[];
  granularity?: string;
}

interface AdminBusiness {
  _id: string; name: string; status: string; type: string;
  contact: { bookingMode?: string; bookingUrl?: string };
  isPartnerListing: boolean; partnerSince?: string; isTestAccount?: boolean;
  createdAt: string; leadsLast30d: number;
  location?: { address?: { city?: string; country?: string } };
}

interface AdminUser {
  _id: string; name: string; email: string; role: string;
  isVerified: boolean; isTestAccount?: boolean; created: string; lastLogin?: string;
}

interface AdminLead {
  _id: string; businessId: { _id: string; name: string } | string | null;
  eventType: string; source: string; timestamp: string;
  converted: boolean;
}

interface InboxTaskRow {
  _id: string; threadId: string; company: string; fromEmail: string; snippet: string;
  lastInboundAt: string; sentiment: string; action: string; status: string;
}

interface InboxData {
  tasks: InboxTaskRow[]; lastSyncedAt: string | null;
  counts: { _id: { status: string; action: string }; count: number }[];
}

type Tab = 'overview' | 'businesses' | 'users' | 'leads' | 'inbox' | 'playbook' | 'features';

// ─── helpers ─────────────────────────────────────────────────────────────────

function decodeRole(token: string): string {
  try { return JSON.parse(atob(token.split('.')[1])).role ?? ''; }
  catch { return ''; }
}

async function apiFetch<T>(path: string, token: string): Promise<T> {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

function isoDate(d: Date) { return d.toISOString().slice(0, 10); }

function autoGranularity(from: string, to: string): Granularity {
  const diff = new Date(to).getTime() - new Date(from).getTime();
  const days = diff / 86_400_000;
  if (days <= 2) return 'hour';
  if (days <= 90) return 'day';
  if (days <= 365) return 'week';
  return 'month';
}

function formatLabel(raw: string, g: Granularity): string {
  if (g === 'hour')  return raw.slice(11) + ':00';
  if (g === 'day')   return raw.slice(5);
  if (g === 'week')  return raw.slice(5);
  if (g === 'month') {
    const [y, m] = raw.split('-');
    return new Date(Number(y), Number(m) - 1).toLocaleString('en', { month: 'short', year: '2-digit' });
  }
  return raw;
}

// ─── shared UI ───────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <div className="w-7 h-7 border-[3px] rounded-full animate-spin"
        style={{ borderColor: `${PRIMARY}30`, borderTopColor: PRIMARY }} />
    </div>
  );
}

function Pagination({ page, pages, onChange }: { page: number; pages: number; onChange: (p: number) => void }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 mt-3 text-sm">
      <button disabled={page === 1} onClick={() => onChange(page - 1)}
        className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors text-xs font-medium">
        ← Prev
      </button>
      <span className="text-slate-400 text-xs font-medium">{page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => onChange(page + 1)}
        className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition-colors text-xs font-medium">
        Next →
      </button>
    </div>
  );
}

function filterClass() {
  return 'px-3 py-2 text-sm rounded-lg border border-slate-200/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 bg-white text-slate-700';
}

function TestBadge() {
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200">
      Test
    </span>
  );
}

// ─── StatCard ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon, iconBg }: {
  label: string; value: number | string; sub?: string;
  icon: React.ReactNode; iconBg: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 p-5 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-sm ${iconBg}`}>
          {icon}
        </div>
      </div>
      <p className="text-3xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1 font-medium">{sub}</p>}
    </div>
  );
}

// ─── Key Terms ───────────────────────────────────────────────────────────────

// ─── Drawer shell ────────────────────────────────────────────────────────────

function Drawer({ title, open, onClose, children, wide }: {
  title: string; open: boolean; onClose: () => void; children: React.ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      {/* backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* panel */}
      <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} bg-white shadow-2xl flex flex-col h-full overflow-hidden animate-slide-in`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100"
          style={{ backgroundColor: PRIMARY }}>
          <h2 className="font-semibold text-white text-base">{title}</h2>
          <button onClick={onClose} className="text-emerald-200 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

// ─── Admin Image Uploader (logo / cover / gallery) ────────────────────────────
// Same signed-Cloudinary-widget mechanism as the business-facing Settings page
// (components/business/features/settings/BusinessImageUpload.tsx), just pointed
// at the admin-scoped /admin/business/:businessId/images/* endpoints so it can
// act on any business, not only the logged-in business's own account.

interface BusinessImages {
  logo?: string;
  cover?: string;
  gallery: string[];
}

function AdminImageUploader({ businessId, token, images, onImagesUpdate }: {
  businessId: string; token: string; images: BusinessImages; onImagesUpdate: (images: BusinessImages) => void;
}) {
  const [uploading, setUploading] = useState<{ logo?: boolean; cover?: boolean; gallery?: boolean }>({});
  const [uploadError, setUploadError] = useState('');

  const openWidget = async (type: 'logo' | 'cover' | 'gallery') => {
    setUploading(u => ({ ...u, [type]: true }));
    setUploadError('');
    try {
      const sigRes = await fetch(`${API}/admin/business/${businessId}/images/upload-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folder: `business/${businessId}/${type}` }),
      });
      const sigData = await sigRes.json();
      if (!sigRes.ok) { setUploadError(sigData.error ?? 'Could not start upload.'); setUploading(u => ({ ...u, [type]: false })); return; }
      const { signature, timestamp, cloudName, apiKey } = sigData.data;

      const widgetOptions: CloudinaryUploadWidgetOptions = {
        cloudName, apiKey, signature, timestamp,
        uploadPreset: 'business_images',
        folder: `business/${businessId}/${type}`,
        sources: ['local', 'url', 'camera'],
        multiple: type === 'gallery',
        maxFiles: type === 'gallery' ? 10 : 1,
        styles: {
          palette: {
            window: '#FFFFFF', windowBorder: '#90A0B3', tabIcon: PRIMARY, menuIcons: '#5A616A',
            textDark: '#000000', textLight: '#FFFFFF', link: PRIMARY, action: '#FF620C',
            inactiveTabIcon: '#0E2F5A', error: '#F44235', inProgress: PRIMARY, complete: '#20B832',
            sourceBg: '#E4EBF1',
          },
        },
      };

      const widget = window.cloudinary.createUploadWidget(widgetOptions, async (error: any, result: any) => {
        if (error) { setUploadError('Upload failed.'); setUploading(u => ({ ...u, [type]: false })); return; }
        if (result.event === 'success') {
          const imageUrl = result.info.secure_url;
          const endpoint = type === 'gallery' ? `/admin/business/${businessId}/images/gallery` : `/admin/business/${businessId}/images/${type}`;
          const res = await fetch(`${API}${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ imageUrl }),
          });
          const d = await res.json();
          if (res.ok) onImagesUpdate(d.data);
          else setUploadError(d.error ?? 'Could not save image.');
        }
        if (result.event === 'close') setUploading(u => ({ ...u, [type]: false }));
      });
      widget.open();
    } catch {
      setUploadError('Could not reach the upload service.');
      setUploading(u => ({ ...u, [type]: false }));
    }
  };

  const removeGalleryImage = async (imageUrl: string) => {
    try {
      const res = await fetch(`${API}/admin/business/${businessId}/images/gallery`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ imageUrl }),
      });
      const d = await res.json();
      if (res.ok) onImagesUpdate(d.data);
    } catch { /* best-effort */ }
  };

  return (
    <div className="space-y-4">
      <SectionLabel>Photos</SectionLabel>
      {uploadError && <p className="text-xs text-red-500 font-medium">{uploadError}</p>}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Logo">
          {images.logo ? (
            <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-slate-200/60 shadow-sm group">
              <img src={images.logo} alt="Logo" className="w-full h-full object-cover" />
              <button type="button" onClick={() => openWidget('logo')}
                className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                <Upload className="w-5 h-5 text-white" />
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => openWidget('logo')} disabled={uploading.logo}
              className="w-full aspect-square rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-emerald-700 hover:border-emerald-300 transition-colors">
              {uploading.logo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-xs font-medium">{uploading.logo ? 'Uploading…' : 'Upload logo'}</span>
            </button>
          )}
        </Field>
        <Field label="Cover">
          {images.cover ? (
            <div className="relative w-full aspect-square rounded-lg overflow-hidden border border-slate-200/60 shadow-sm group">
              <img src={images.cover} alt="Cover" className="w-full h-full object-cover" />
              <button type="button" onClick={() => openWidget('cover')}
                className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                <Upload className="w-5 h-5 text-white" />
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => openWidget('cover')} disabled={uploading.cover}
              className="w-full aspect-square rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-emerald-700 hover:border-emerald-300 transition-colors">
              {uploading.cover ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-xs font-medium">{uploading.cover ? 'Uploading…' : 'Upload cover'}</span>
            </button>
          )}
        </Field>
      </div>
      <Field label={`Gallery${images.gallery?.length ? ` (${images.gallery.length})` : ''}`}>
        <div className="grid grid-cols-4 gap-3">
          {images.gallery?.map(url => (
            <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200/60 shadow-sm group">
              <img src={url} alt="Gallery" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeGalleryImage(url)}
                className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => openWidget('gallery')} disabled={uploading.gallery}
            className="aspect-square rounded-lg border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-emerald-700 hover:border-emerald-300 transition-colors">
            {uploading.gallery ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          </button>
        </div>
      </Field>
    </div>
  );
}

// ─── Edit Business Drawer ─────────────────────────────────────────────────────

interface BusinessDetail {
  _id: string; name: string; status: string; type: string;
  contact: { bookingMode?: string; bookingUrl?: string; website?: string; email?: string; phone?: string };
  isPartnerListing: boolean; isTestAccount?: boolean;
  location?: { address?: { city?: string; country?: string } };
  createdAt?: string;
  images?: BusinessImages;
}

function EditBusinessDrawer({ businessId, token, onClose, onSaved }: {
  businessId: string | null; token: string; onClose: () => void; onSaved: () => void;
}) {
  const [data,    setData]    = useState<BusinessDetail | null>(null);
  const [form,    setForm]    = useState<Partial<BusinessDetail & { bookingMode: string; bookingUrl: string; website: string; contactEmail: string; contactPhone: string }>>({});
  const [images,  setImages]  = useState<BusinessImages>({ gallery: [] });
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  // Delete flow
  const [deleteStep,    setDeleteStep]    = useState<'idle' | 'confirm'>('idle');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting,      setDeleting]      = useState(false);
  const [deleteError,   setDeleteError]   = useState('');

  useEffect(() => {
    if (!businessId) return;
    setLoading(true); setError(''); setDeleteStep('idle'); setDeleteConfirm(''); setDeleteError('');
    fetch(`${API}/admin/business/${businessId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const b = d.business as BusinessDetail;
        setData(b);
        setImages(b.images ?? { gallery: [] });
        setForm({
          name:         b.name,
          status:       b.status,
          type:         b.type,
          bookingMode:  b.contact?.bookingMode ?? 'platform',
          bookingUrl:   b.contact?.bookingUrl ?? '',
          website:      b.contact?.website ?? '',
          contactEmail: b.contact?.email ?? '',
          contactPhone: b.contact?.phone ?? '',
          isPartnerListing: b.isPartnerListing,
          isTestAccount: b.isTestAccount ?? false,
        });
      })
      .catch(() => setError('Could not load business.'))
      .finally(() => setLoading(false));
  }, [businessId, token]);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/admin/business/${businessId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error ?? 'Save failed'); return; }
      onSaved();
      onClose();
    } catch { setError('Save failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirm.trim() !== data?.name) {
      setDeleteError('Name does not match.'); return;
    }
    setDeleting(true); setDeleteError('');
    try {
      const res = await fetch(`${API}/admin/business/${businessId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirmName: deleteConfirm.trim() }),
      });
      const d = await res.json();
      if (!res.ok) { setDeleteError(d.error ?? 'Delete failed'); setDeleting(false); return; }
      onSaved(); onClose();
    } catch { setDeleteError('Delete failed.'); setDeleting(false); }
  };

  const inp = 'w-full px-3 py-2 text-sm rounded-lg border border-slate-200/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white';
  const sel = inp;

  return (
    <Drawer title={data ? `Edit: ${data.name}` : 'Edit Business'} open={!!businessId} onClose={onClose}>
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-[3px] rounded-full animate-spin" style={{ borderColor: `${PRIMARY}30`, borderTopColor: PRIMARY }} /></div>
      ) : error && !data ? (
        <p className="p-6 text-red-500 text-sm">{error}</p>
      ) : data && (
        <div className="p-6 space-y-5">

          {/* Read-only info */}
          <div className="bg-slate-50 rounded-xl px-4 py-3 text-xs text-slate-400 space-y-1">
            <p><span className="font-medium text-slate-500">ID:</span> {data._id}</p>
            <p><span className="font-medium text-slate-500">Location:</span> {data.location?.address?.city}, {data.location?.address?.country}</p>
            <p><span className="font-medium text-slate-500">Created:</span> {data.createdAt ? format(new Date(data.createdAt), 'MMM d, yyyy') : '—'}</p>
          </div>

          <AdminImageUploader businessId={data._id} token={token} images={images} onImagesUpdate={setImages} />

          <Field label="Business Name">
            <input className={inp} value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <select className={sel} value={form.status ?? ''} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </Field>
            <Field label="Type">
              <select className={sel} value={form.type ?? ''} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                <option value="tour_operator">Tour Operator</option>
                <option value="sanctuary">Sanctuary</option>
                <option value="national_park">National Park</option>
                <option value="adventure_company">Adventure Company</option>
              </select>
            </Field>
          </div>

          <Field label="Contact Email">
            <input type="email" className={inp} value={form.contactEmail ?? ''} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} />
          </Field>

          <Field label="Contact Phone">
            <input type="tel" className={inp} value={form.contactPhone ?? ''} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} />
          </Field>

          <Field label="Website">
            <input type="url" className={inp} value={form.website ?? ''} placeholder="https://" onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
          </Field>

          <Field label="Booking Mode">
            <div className="flex gap-3">
              {(['platform', 'redirect'] as const).map(m => (
                <button key={m} onClick={() => setForm(f => ({ ...f, bookingMode: m }))}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                    form.bookingMode === m
                      ? 'text-white border-transparent shadow-sm'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  style={form.bookingMode === m ? { backgroundColor: PRIMARY } : {}}>
                  {m === 'platform' ? '🏠 Platform' : '🔗 Redirect'}
                </button>
              ))}
            </div>
          </Field>

          {form.bookingMode === 'redirect' && (
            <Field label="Booking URL" hint="Visitors will be sent here with ?ref=wildy appended">
              <input type="url" className={inp} value={form.bookingUrl ?? ''} placeholder="https://yoursite.com/book"
                onChange={e => setForm(f => ({ ...f, bookingUrl: e.target.value }))} />
            </Field>
          )}

          <Field label="Partner Listing">
            <div className="flex items-center gap-3">
              <button onClick={() => setForm(f => ({ ...f, isPartnerListing: !f.isPartnerListing }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isPartnerListing ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isPartnerListing ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-slate-600">{form.isPartnerListing ? 'Active partner — leads are tracked' : 'Not a partner listing'}</span>
            </div>
          </Field>

          <Field label="Test Account" hint="Internal use only. Visibility on the public site is controlled by Status, not this flag.">
            <div className="flex items-center gap-3">
              <button onClick={() => setForm(f => ({ ...f, isTestAccount: !f.isTestAccount }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isTestAccount ? 'bg-amber-500' : 'bg-slate-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isTestAccount ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-slate-600">{form.isTestAccount ? 'Marked as test — excluded from Overview stats' : 'Not a test account'}</span>
            </div>
          </Field>

          {error && <p className="text-sm text-red-500 font-medium">{error}</p>}

          {/* Save */}
          <button onClick={handleSave} disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md disabled:opacity-60 transition-opacity"
            style={{ backgroundColor: PRIMARY }}>
            {saving ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>

          {/* Delete zone */}
          <div className="mt-2 pt-5 border-t border-slate-100">
            {deleteStep === 'idle' ? (
              <button onClick={() => setDeleteStep('confirm')}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition-colors">
                <Trash2 className="w-4 h-4" /> Delete this business
              </button>
            ) : (
              <div className="space-y-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-2 text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p className="text-sm font-semibold">This is permanent. Type the business name to confirm:</p>
                </div>
                <p className="text-xs text-red-500 font-mono bg-red-100 px-2 py-1 rounded">{data.name}</p>
                <input className="w-full px-3 py-2 text-sm rounded-lg border border-red-300 focus:outline-none focus:ring-2 focus:ring-red-400"
                  placeholder="Type exact name here…"
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)} />
                {deleteError && <p className="text-xs text-red-600 font-medium">{deleteError}</p>}
                <div className="flex gap-2">
                  <button onClick={() => { setDeleteStep('idle'); setDeleteConfirm(''); setDeleteError(''); }}
                    className="flex-1 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleDelete} disabled={deleting || deleteConfirm.trim() !== data.name}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-40 transition-colors">
                    {deleting ? 'Deleting…' : <><Trash2 className="w-3.5 h-3.5" /> Confirm Delete</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}

// ─── Edit User Drawer ─────────────────────────────────────────────────────────

interface UserDetail {
  _id: string; name: string; email: string; role: string;
  isVerified: boolean; isTestAccount?: boolean; created: string; lastLogin?: string;
}

function EditUserDrawer({ userId, token, onClose, onSaved }: {
  userId: string | null; token: string; onClose: () => void; onSaved: () => void;
}) {
  const [data,    setData]    = useState<UserDetail | null>(null);
  const [form,    setForm]    = useState<Partial<UserDetail>>({});
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!userId) return;
    setLoading(true); setError('');
    // Re-use admin users list filtered by a quick search isn't ideal;
    // fetch from the existing users endpoint and find the right one
    fetch(`${API}/admin/users?limit=200`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const user = d.users?.find((u: UserDetail) => u._id === userId);
        if (!user) { setError('User not found'); return; }
        setData(user);
        setForm({ name: user.name, role: user.role, isVerified: user.isVerified, isTestAccount: user.isTestAccount ?? false });
      })
      .catch(() => setError('Could not load user.'))
      .finally(() => setLoading(false));
  }, [userId, token]);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/admin/user/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error ?? 'Save failed'); return; }
      onSaved(); onClose();
    } catch { setError('Save failed.'); }
    finally { setSaving(false); }
  };

  const inp = 'w-full px-3 py-2 text-sm rounded-lg border border-slate-200/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white';

  return (
    <Drawer title={data ? `Edit: ${data.name}` : 'Edit User'} open={!!userId} onClose={onClose}>
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-[3px] rounded-full animate-spin" style={{ borderColor: `${PRIMARY}30`, borderTopColor: PRIMARY }} /></div>
      ) : error && !data ? (
        <p className="p-6 text-red-500 text-sm">{error}</p>
      ) : data && (
        <div className="p-6 space-y-5">

          <div className="bg-slate-50 rounded-xl px-4 py-3 text-xs text-slate-400 space-y-1">
            <p><span className="font-medium text-slate-500">ID:</span> {data._id}</p>
            <p><span className="font-medium text-slate-500">Email:</span> {data.email} <span className="text-slate-300">(not editable)</span></p>
            <p><span className="font-medium text-slate-500">Signed up:</span> {data.created ? format(new Date(data.created), 'MMM d, yyyy') : '—'}</p>
            <p><span className="font-medium text-slate-500">Last login:</span> {data.lastLogin ? format(new Date(data.lastLogin), 'MMM d, HH:mm') : '—'}</p>
          </div>

          <Field label="Name">
            <input className={inp} value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </Field>

          <Field label="Role" hint={form.role === 'admin' ? '⚠️ Admin users have full access to this internal tool' : undefined}>
            <select className={inp} value={form.role ?? ''} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              <option value="user">User — public visitor</option>
              <option value="business">Business — dashboard access</option>
              <option value="admin">Admin ⚠️ — full internal tool access</option>
            </select>
          </Field>

          <Field label="Email Verified">
            <div className="flex items-center gap-3">
              <button onClick={() => setForm(f => ({ ...f, isVerified: !f.isVerified }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isVerified ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isVerified ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-slate-600">{form.isVerified ? 'Verified' : 'Not verified'}</span>
            </div>
          </Field>

          <Field label="Test Account" hint="Internal use only, excludes this user from Overview stats.">
            <div className="flex items-center gap-3">
              <button onClick={() => setForm(f => ({ ...f, isTestAccount: !f.isTestAccount }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isTestAccount ? 'bg-amber-500' : 'bg-slate-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isTestAccount ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-slate-600">{form.isTestAccount ? 'Marked as test' : 'Not a test account'}</span>
            </div>
          </Field>

          {error && <p className="text-sm text-red-500 font-medium">{error}</p>}

          <button onClick={handleSave} disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md disabled:opacity-60 transition-opacity"
            style={{ backgroundColor: PRIMARY }}>
            {saving ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>
        </div>
      )}
    </Drawer>
  );
}

// ─── New Customer Drawer ──────────────────────────────────────────────────────

interface NewCustomerForm {
  name: string; emoji: string; type: string; description: string; status: string;
  street: string; city: string; state: string; country: string; postalCode: string;
  latitude: string; longitude: string;
  contactEmail: string; contactPhone: string; website: string; whatsapp: string;
  bookingMode: 'platform' | 'redirect'; bookingUrl: string;
  hasAnimalActivities: boolean; hasAdventureActivities: boolean; isPartnerListing: boolean; isTestAccount: boolean;
  ownerName: string; ownerEmail: string; ownerPhone: string; password: string;
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
  const bytes = new Uint32Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => chars[b % chars.length]).join('');
}

function emptyCustomerForm(): NewCustomerForm {
  return {
    name: '', emoji: '🌿', type: '', description: '', status: 'active',
    street: '', city: '', state: '', country: '', postalCode: '',
    latitude: '', longitude: '',
    contactEmail: '', contactPhone: '', website: '', whatsapp: '',
    bookingMode: 'redirect', bookingUrl: '',
    hasAnimalActivities: true, hasAdventureActivities: false, isPartnerListing: true, isTestAccount: false,
    ownerName: '', ownerEmail: '', ownerPhone: '', password: generatePassword(),
  };
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-700/70 pt-2 first:pt-0">{children}</h3>;
}

function NewCustomerDrawer({ open, token, onClose, onSaved }: {
  open: boolean; token: string; onClose: () => void; onSaved: () => void;
}) {
  const [form,      setForm]      = useState<NewCustomerForm>(emptyCustomerForm);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [geoResult, setGeoResult] = useState('');
  const [copied,    setCopied]    = useState(false);
  const [created,   setCreated]   = useState<{ businessId: string; name: string; email: string; password: string } | null>(null);
  const [images,    setImages]    = useState<BusinessImages>({ gallery: [] });

  useEffect(() => {
    if (open) { setForm(emptyCustomerForm()); setError(''); setGeoResult(''); setCreated(null); setImages({ gallery: [] }); }
  }, [open]);

  const set = <K extends keyof NewCustomerForm>(key: K, value: NewCustomerForm[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const findCoordinates = async () => {
    const address = [form.street, form.city, form.state, form.country].filter(Boolean).join(', ');
    if (!address) { setGeoResult('Enter at least a city and country first.'); return; }
    setGeocoding(true); setGeoResult('');
    try {
      const res = await fetch(`${API}/geocode?address=${encodeURIComponent(address)}`);
      const d = await res.json();
      if (!res.ok) { setGeoResult(d.error ?? 'Could not find that address.'); return; }
      set('latitude', String(d.lat)); set('longitude', String(d.lng));
      setGeoResult(`Found: ${d.display_name}`);
    } catch { setGeoResult('Could not reach geocoding service.'); }
    finally { setGeocoding(false); }
  };

  const canSubmit = !!(form.name && form.type && form.description && form.contactEmail && form.contactPhone
    && form.street && form.city && form.state && form.country && form.postalCode && form.latitude && form.longitude
    && form.ownerName && form.ownerEmail && form.password.length >= 8);

  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/admin/customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          business: {
            name: form.name, emoji: form.emoji, type: form.type, description: form.description, status: form.status,
            hasAnimalActivities: form.hasAnimalActivities, hasAdventureActivities: form.hasAdventureActivities,
            isPartnerListing: form.isPartnerListing,
            isTestAccount: form.isTestAccount,
            contact: {
              email: form.contactEmail, phone: form.contactPhone, website: form.website || undefined,
              whatsappNumber: form.whatsapp || undefined, bookingMode: form.bookingMode,
              bookingUrl: form.bookingMode === 'redirect' ? form.bookingUrl : undefined,
            },
            location: {
              address: { street: form.street, city: form.city, state: form.state, country: form.country, postalCode: form.postalCode },
              latitude: Number(form.latitude), longitude: Number(form.longitude),
            },
          },
          user: { name: form.ownerName, email: form.ownerEmail, phone: form.ownerPhone || undefined, password: form.password },
        }),
      });
      const d = await res.json();
      if (!res.ok) { setError(d.error ?? 'Could not create customer.'); return; }
      setCreated({ businessId: d.business._id, name: form.name, email: form.ownerEmail, password: form.password });
      onSaved();
    } catch { setError('Could not reach the server.'); }
    finally { setSaving(false); }
  };

  const copyCreds = () => {
    if (!created) return;
    navigator.clipboard.writeText(`Login: ${created.email}\nPassword: ${created.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inp = 'w-full px-3 py-2 text-sm rounded-lg border border-slate-200/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white';

  return (
    <Drawer title="New Customer" open={open} onClose={onClose} wide>
      {created ? (
        <div className="p-6 space-y-5">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center space-y-2">
            <CheckCircle2 className="w-9 h-9 text-emerald-600 mx-auto" />
            <p className="font-semibold text-emerald-800">"{created.name}" created</p>
            <p className="text-sm text-emerald-700">Send these login details to the business:</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 space-y-2 font-mono text-sm">
            <p><span className="text-slate-400">Email:</span> {created.email}</p>
            <p><span className="text-slate-400">Password:</span> {created.password}</p>
          </div>

          <AdminImageUploader businessId={created.businessId} token={token} images={images} onImagesUpdate={setImages} />

          <button onClick={copyCreds}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md transition-opacity"
            style={{ backgroundColor: PRIMARY }}>
            <Copy className="w-4 h-4" /> {copied ? 'Copied!' : 'Copy login details'}
          </button>
          <button onClick={onClose}
            className="w-full py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
            Done
          </button>
        </div>
      ) : (
        <div className="p-6 space-y-4">

          <SectionLabel>Business basics</SectionLabel>
          <div className="grid grid-cols-[1fr_88px] gap-3">
            <Field label="Business Name">
              <input className={inp} value={form.name} onChange={e => set('name', e.target.value)} />
            </Field>
            <Field label="Emoji">
              <input className={inp + ' text-center text-lg'} value={form.emoji} maxLength={4} onChange={e => set('emoji', e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Type">
              <select className={inp} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="">Select…</option>
                <option value="tour_operator">Tour Operator</option>
                <option value="sanctuary">Sanctuary</option>
                <option value="national_park">National Park</option>
                <option value="adventure_company">Adventure Company</option>
              </select>
            </Field>
            <Field label="Status">
              <select className={inp} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
            </Field>
          </div>
          <Field label="Description">
            <textarea className={inp} rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
          </Field>

          <SectionLabel>Location</SectionLabel>
          <p className="text-xs text-slate-400 -mt-2">All fields required — Wildy stores structured addresses, not free text.</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Street"><input className={inp} value={form.street} onChange={e => set('street', e.target.value)} /></Field>
            <Field label="City"><input className={inp} value={form.city} onChange={e => set('city', e.target.value)} /></Field>
            <Field label="State / Region"><input className={inp} value={form.state} onChange={e => set('state', e.target.value)} /></Field>
            <Field label="Country"><input className={inp} value={form.country} onChange={e => set('country', e.target.value)} /></Field>
            <Field label="Postal Code"><input className={inp} value={form.postalCode} onChange={e => set('postalCode', e.target.value)} /></Field>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1"><Field label="Latitude"><input className={inp} value={form.latitude} onChange={e => set('latitude', e.target.value)} /></Field></div>
            <div className="flex-1"><Field label="Longitude"><input className={inp} value={form.longitude} onChange={e => set('longitude', e.target.value)} /></Field></div>
            <button onClick={findCoordinates} disabled={geocoding} type="button"
              className="mb-0.5 shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50">
              <MapPin className="w-3.5 h-3.5" /> {geocoding ? 'Looking…' : 'Find'}
            </button>
          </div>
          {geoResult && <p className="text-xs text-slate-400">{geoResult}</p>}

          <SectionLabel>Contact &amp; booking</SectionLabel>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Contact Email"><input type="email" className={inp} value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} /></Field>
            <Field label="Contact Phone"><input type="tel" className={inp} value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Website"><input type="url" className={inp} placeholder="https://" value={form.website} onChange={e => set('website', e.target.value)} /></Field>
            <Field label="WhatsApp"><input className={inp} value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} /></Field>
          </div>
          <Field label="Booking Mode">
            <div className="flex gap-3">
              {(['redirect', 'platform'] as const).map(m => (
                <button key={m} type="button" onClick={() => set('bookingMode', m)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                    form.bookingMode === m ? 'text-white border-transparent shadow-sm' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  style={form.bookingMode === m ? { backgroundColor: PRIMARY } : {}}>
                  {m === 'platform' ? '🏠 Platform' : '🔗 Redirect'}
                </button>
              ))}
            </div>
          </Field>
          {form.bookingMode === 'redirect' && (
            <Field label="Booking URL" hint="Visitors will be sent here with ?ref=wildy appended">
              <input type="url" className={inp} placeholder="https://yoursite.com/book" value={form.bookingUrl} onChange={e => set('bookingUrl', e.target.value)} />
            </Field>
          )}

          <SectionLabel>Flags</SectionLabel>
          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.hasAnimalActivities} onChange={e => set('hasAnimalActivities', e.target.checked)}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
              Animal activities
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.hasAdventureActivities} onChange={e => set('hasAdventureActivities', e.target.checked)}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
              Adventure activities
            </label>
            <label className="flex items-center gap-2 text-sm text-amber-700">
              <input type="checkbox" checked={form.isTestAccount} onChange={e => set('isTestAccount', e.target.checked)}
                className="rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
              This is a test account
            </label>
          </div>
          <Field label="Partner Listing">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => set('isPartnerListing', !form.isPartnerListing)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isPartnerListing ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.isPartnerListing ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-slate-600">{form.isPartnerListing ? 'Active partner — leads are tracked' : 'Not a partner listing'}</span>
            </div>
          </Field>

          <SectionLabel>Owner login</SectionLabel>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Owner Name"><input className={inp} value={form.ownerName} onChange={e => set('ownerName', e.target.value)} /></Field>
            <Field label="Owner Phone"><input type="tel" className={inp} value={form.ownerPhone} onChange={e => set('ownerPhone', e.target.value)} /></Field>
          </div>
          <Field label="Owner Email (login)"><input type="email" className={inp} value={form.ownerEmail} onChange={e => set('ownerEmail', e.target.value)} /></Field>
          <Field label="Password">
            <div className="flex gap-2">
              <input className={inp + ' font-mono'} value={form.password} onChange={e => set('password', e.target.value)} />
              <button type="button" onClick={() => set('password', generatePassword())}
                title="Regenerate" className="shrink-0 px-3 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </Field>

          {error && <p className="text-sm text-red-500 font-medium">{error}</p>}

          <button onClick={handleSubmit} disabled={saving || !canSubmit}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md disabled:opacity-50 transition-opacity"
            style={{ backgroundColor: PRIMARY }}>
            {saving ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Creating…</> : <><Plus className="w-4 h-4" /> Create Customer</>}
          </button>
        </div>
      )}
    </Drawer>
  );
}

const TERMS = [
  { term: 'Lead',            def: 'Any recorded interaction a visitor has with a business that originated from Wildy — a profile view, a map pin click, or a "Visit Website" click. One session can generate multiple lead events.' },
  { term: 'Profile View',    def: "A visitor opened a business's public profile page on Wildy. Tracked automatically when the page loads." },
  { term: 'Map Click',       def: 'A visitor clicked a business pin on the Wildy map. Tracked before the business card expands.' },
  { term: 'Booking Click',   def: 'A visitor clicked the "Visit Website" button on a redirect-mode business. The strongest lead signal — the visitor actively chose to visit the partner\'s site.' },
  { term: 'Conversion',      def: 'A visitor who clicks through to a partner site and later books on Wildy (not currently active — all businesses are in redirect mode, so bookings happen on partner\'s own site).' },
  { term: 'Conversion Rate', def: 'Not currently tracked. Would apply when platform-mode businesses (booking on Wildy) are active.' },
  { term: 'Partner Listing', def: 'A business with a commercial agreement with Wildy. Their leads are tracked, billed, and visible in the Partner Stats dashboard.' },
  { term: '?ref=wildy',      def: 'URL parameter appended to every redirect link (e.g. https://partner.com/book?ref=wildy). Partners can verify our traffic independently in their own Google Analytics.' },
  { term: 'Session ID',      def: 'An anonymous UUID stored in the visitor\'s browser. Links a lead click to a later booking without storing any personal data.' },
];

function KeyTerms() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-semibold text-slate-700 hover:bg-slate-50/80 transition-colors">
        <span className="flex items-center gap-2">
          <span className="text-base">📖</span>
          Key Terms &amp; Definitions
        </span>
        <span className="text-slate-400 text-xs">{open ? '▲ collapse' : '▼ expand'}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-50">
          {TERMS.map(({ term, def }) => (
            <div key={term} className="px-6 py-3.5 grid grid-cols-[200px_1fr] gap-6 items-start">
              <span className="font-semibold text-sm text-slate-800 pt-0.5">{term}</span>
              <span className="text-sm text-slate-500 leading-relaxed">{def}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ token }: { token: string }) {
  const today   = isoDate(new Date());
  const ago30   = isoDate(subDays(new Date(), 30));

  const [from,        setFrom]        = useState(ago30);
  const [to,          setTo]          = useState(today);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [autoG,       setAutoG]       = useState(true); // whether granularity follows date range
  const [stats,       setStats]       = useState<AdminStats | null>(null);
  const [loading,     setLoading]     = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<AdminStats>(
      `/admin/stats?from=${from}&to=${to}&granularity=${granularity}`,
      token
    ).then(setStats).catch(() => {}).finally(() => setLoading(false));
  }, [token, from, to, granularity]);

  useEffect(() => { load(); }, [load]);

  const handleFromChange = (v: string) => {
    setFrom(v);
    if (autoG) setGranularity(autoGranularity(v, to));
  };
  const handleToChange = (v: string) => {
    setTo(v);
    if (autoG) setGranularity(autoGranularity(from, v));
  };
  const handleGranularity = (g: Granularity) => {
    setGranularity(g);
    setAutoG(false);
  };

  const gLabels: { id: Granularity; label: string }[] = [
    { id: 'hour', label: 'Hourly' },
    { id: 'day',  label: 'Daily' },
    { id: 'week', label: 'Weekly' },
    { id: 'month',label: 'Monthly' },
  ];

  const chartData = (stats?.dailyLeads ?? []).map(d => ({
    ...d,
    label: formatLabel(d.date, granularity),
  }));

  return (
    <div className="space-y-5">

      {/* Date range + granularity controls */}
      <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 px-5 py-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">From</label>
          <input type="date" value={from} max={to} onChange={e => handleFromChange(e.target.value)}
            className={filterClass()} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">To</label>
          <input type="date" value={to} min={from} max={today} onChange={e => handleToChange(e.target.value)}
            className={filterClass()} />
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs font-semibold text-slate-400 mr-1">Group by</span>
          {gLabels.map(g => (
            <button key={g.id} onClick={() => handleGranularity(g.id)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                granularity === g.id
                  ? 'text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200'
              }`}
              style={granularity === g.id ? { backgroundColor: PRIMARY } : {}}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Users" value={stats?.users.total ?? '—'}
          sub={stats ? `+${stats.users.newThisWeek} this week` : undefined}
          icon={<Users className="w-4 h-4 text-blue-600" />}
          iconBg="bg-blue-100 border border-blue-200/50" />
        <StatCard
          label="Businesses" value={stats?.businesses.total ?? '—'}
          sub={stats ? `${stats.businesses.active} active` : undefined}
          icon={<Building2 className="w-4 h-4 text-emerald-600" />}
          iconBg="bg-emerald-100 border border-emerald-200/50" />
        <StatCard
          label="Leads (period)" value={stats?.leads.total ?? '—'}
          icon={<TrendingUp className="w-4 h-4 text-violet-600" />}
          iconBg="bg-violet-100 border border-violet-200/50" />
        <StatCard
          label="Bookings (7d)" value={stats?.bookings.newThisWeek ?? '—'}
          sub="new this week"
          icon={<BookOpen className="w-4 h-4 text-amber-600" />}
          iconBg="bg-amber-100 border border-amber-200/50" />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Profile Views" value={stats?.leads.profileViews ?? '—'}
          icon={<Eye className="w-4 h-4 text-blue-500" />}
          iconBg="bg-blue-50 border border-blue-200/50" />
        <StatCard
          label="Booking Clicks" value={stats?.leads.bookingClicks ?? '—'}
          icon={<MousePointerClick className="w-4 h-4 text-violet-500" />}
          iconBg="bg-violet-50 border border-violet-200/50" />
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">
            Leads &amp; Conversions —{' '}
            <span className="text-slate-400 font-normal">
              {format(new Date(from), 'MMM d')} → {format(new Date(to), 'MMM d, yyyy')}
            </span>
          </h3>
          {loading && (
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${PRIMARY}40`, borderTopColor: PRIMARY }} />
          )}
        </div>
        {!loading && chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                labelStyle={{ fontWeight: 600, color: '#334155' }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Line type="monotone" dataKey="leads" stroke={PRIMARY} strokeWidth={2.5} dot={false} name="Leads" />
            </LineChart>
          </ResponsiveContainer>
        ) : !loading ? (
          <p className="text-slate-400 text-sm text-center py-12">No lead data for this period.</p>
        ) : (
          <div className="h-[260px] flex items-center justify-center">
            <Spinner />
          </div>
        )}
      </div>

      <CountryBreakdown rows={stats?.byCountry ?? []} loading={loading} />

      <KeyTerms />
    </div>
  );
}

// ─── Country breakdown ─────────────────────────────────────────────────────────

function CountryBreakdown({ rows, loading }: {
  rows: { country: string; businesses: number; users: number }[];
  loading: boolean;
}) {
  const maxBusinesses = Math.max(1, ...rows.map(r => r.businesses));

  return (
    <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Globe className="w-4 h-4 text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-700">Businesses &amp; Users by Country</h3>
      </div>
      {loading ? <Spinner /> : rows.length > 0 ? (
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80 border-b border-slate-100">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Country</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Businesses</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Users</th>
              <th className="px-4 py-3 w-40"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map(row => (
              <tr key={row.country} className="hover:bg-slate-50/40 transition-colors">
                <td className="px-4 py-3 font-semibold text-slate-800">{row.country}</td>
                <td className="px-4 py-3 text-right text-slate-700 font-medium">{row.businesses}</td>
                <td className="px-4 py-3 text-right text-slate-500">{row.users}</td>
                <td className="px-4 py-3">
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(row.businesses / maxBusinesses) * 100}%`, backgroundColor: PRIMARY }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-slate-400 text-sm text-center py-8">No country data yet.</p>
      )}
      <p className="px-5 py-3 text-xs text-slate-400 border-t border-slate-100">
        "Users" counts business-owner accounts linked to a business in that country. Regular visitor
        accounts aren't tied to a country and aren't included here.
      </p>
    </div>
  );
}

// ─── Businesses tab ───────────────────────────────────────────────────────────

function BusinessesTab({ token }: { token: string }) {
  const [data,       setData]       = useState<{ businesses: AdminBusiness[]; pagination: any } | null>(null);
  const [page,       setPage]       = useState(1);
  const [search,     setSearch]     = useState('');
  const [status,     setStatus]     = useState('');
  const [mode,       setMode]       = useState('');
  const [hideTest,   setHideTest]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [toggling,   setToggling]   = useState<string | null>(null);
  const [editingId,  setEditingId]  = useState<string | null>(null);
  const [creating,   setCreating]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ page: String(page), limit: '20' });
    if (search)   q.set('search', search);
    if (status)   q.set('status', status);
    if (mode)     q.set('bookingMode', mode);
    if (hideTest) q.set('hideTest', 'true');
    apiFetch<typeof data>(`/admin/businesses?${q}`, token)
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [token, page, search, status, mode, hideTest]);

  useEffect(() => { load(); }, [load]);

  const togglePartner = async (biz: AdminBusiness) => {
    setToggling(biz._id);
    try {
      await fetch(`${API}/admin/business/${biz._id}/partner-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isPartnerListing: !biz.isPartnerListing }),
      });
      load();
    } finally { setToggling(null); }
  };

  const cycleStatus = async (biz: AdminBusiness) => {
    const next: Record<string, string> = { pending: 'active', active: 'suspended', suspended: 'pending' };
    setToggling(biz._id + '-status');
    try {
      await fetch(`${API}/admin/business/${biz._id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: next[biz.status] ?? 'active' }),
      });
      load();
    } finally { setToggling(null); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name…" className={filterClass() + ' w-48'} />
        <span className="text-xs font-semibold text-slate-400 whitespace-nowrap">
          {data?.pagination?.total ?? '—'} businesses
        </span>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className={filterClass()}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={mode} onChange={e => { setMode(e.target.value); setPage(1); }} className={filterClass()}>
          <option value="">All booking modes</option>
          <option value="platform">Platform</option>
          <option value="redirect">Redirect</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-500 px-1">
          <input type="checkbox" checked={hideTest} onChange={e => { setHideTest(e.target.checked); setPage(1); }}
            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          Hide test
        </label>
        <button onClick={() => setCreating(true)}
          className="ml-auto flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-sm hover:opacity-90 transition-opacity"
          style={{ backgroundColor: PRIMARY }}>
          <Plus className="w-4 h-4" /> New Customer
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 overflow-hidden">
        {loading ? <Spinner /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>{['Business', 'Status', 'Mode', 'Partner', 'Leads 30d', 'Created', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(data?.businesses ?? []).map(b => (
                <tr key={b._id} className="hover:bg-slate-50/40 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                      {b.name}{b.isTestAccount && <TestBadge />}
                    </p>
                    <p className="text-xs text-slate-400">{b.location?.address?.city}, {b.location?.address?.country}</p>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => cycleStatus(b)} disabled={toggling === b._id + '-status'}
                      title="Click to cycle: pending → active → suspended"
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-opacity hover:opacity-70 ${
                        b.status === 'active'  ? 'bg-emerald-100 text-emerald-700' :
                        b.status === 'pending' ? 'bg-amber-100   text-amber-700'   :
                                                 'bg-red-100     text-red-700'}`}>
                      {toggling === b._id + '-status' ? '…' : b.status}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-medium">{b.contact?.bookingMode ?? '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => togglePartner(b)} disabled={toggling === b._id}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                        b.isPartnerListing ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        b.isPartnerListing ? 'translate-x-4' : 'translate-x-1'}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-700">{b.leadsLast30d}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{b.createdAt ? format(new Date(b.createdAt), 'MMM d, yyyy') : '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setEditingId(b._id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                      title="Edit business">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {(data?.businesses ?? []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">No businesses found.</td></tr>
              )}
            </tbody>
          </table>
        )}
        <div className="px-4 pb-3"><Pagination page={page} pages={data?.pagination?.pages ?? 1} onChange={setPage} /></div>
      </div>

      <EditBusinessDrawer
        businessId={editingId}
        token={token}
        onClose={() => setEditingId(null)}
        onSaved={() => { setEditingId(null); load(); }}
      />

      <NewCustomerDrawer
        open={creating}
        token={token}
        onClose={() => setCreating(false)}
        onSaved={load}
      />
    </div>
  );
}

// ─── Users tab ────────────────────────────────────────────────────────────────

function UsersTab({ token }: { token: string }) {
  const [data,      setData]      = useState<{ users: AdminUser[]; pagination: any } | null>(null);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState('');
  const [role,      setRole]      = useState('');
  const [hideTest,  setHideTest]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ page: String(page), limit: '20' });
    if (search)   q.set('search', search);
    if (role)     q.set('role', role);
    if (hideTest) q.set('hideTest', 'true');
    apiFetch<typeof data>(`/admin/users?${q}`, token)
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [token, page, search, role, hideTest]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name or email…" className={filterClass() + ' w-64'} />
        <span className="text-xs font-semibold text-slate-400 whitespace-nowrap">
          {data?.pagination?.total ?? '—'} users
        </span>
        <select value={role} onChange={e => { setRole(e.target.value); setPage(1); }} className={filterClass()}>
          <option value="">All roles</option>
          <option value="user">User</option>
          <option value="business">Business</option>
          <option value="admin">Admin</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-500 px-1">
          <input type="checkbox" checked={hideTest} onChange={e => { setHideTest(e.target.checked); setPage(1); }}
            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          Hide test
        </label>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 overflow-hidden">
        {loading ? <Spinner /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>{['Name', 'Email', 'Role', 'Verified', 'Signed Up', 'Last Login', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(data?.users ?? []).map(u => (
                <tr key={u._id} className="hover:bg-slate-50/40 transition-colors">
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    <span className="flex items-center gap-1.5">{u.name}{u.isTestAccount && <TestBadge />}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      u.role === 'admin'    ? 'bg-red-100    text-red-700'    :
                      u.role === 'business' ? 'bg-blue-100   text-blue-700'   :
                                             'bg-slate-100  text-slate-600'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-medium">
                    <span className={u.isVerified ? 'text-emerald-600' : 'text-slate-400'}>
                      {u.isVerified ? '✓ Yes' : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{u.created ? format(new Date(u.created), 'MMM d, yyyy') : '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{u.lastLogin ? format(new Date(u.lastLogin), 'MMM d, HH:mm') : '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setEditingId(u._id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                      title="Edit user">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {(data?.users ?? []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400 text-sm">No users found.</td></tr>
              )}
            </tbody>
          </table>
        )}
        <div className="px-4 pb-3"><Pagination page={page} pages={data?.pagination?.pages ?? 1} onChange={setPage} /></div>
      </div>

      <EditUserDrawer
        userId={editingId}
        token={token}
        onClose={() => setEditingId(null)}
        onSaved={() => { setEditingId(null); load(); }}
      />
    </div>
  );
}

// ─── Leads tab ────────────────────────────────────────────────────────────────

function LeadsTab({ token }: { token: string }) {
  const [data,      setData]      = useState<{ leads: AdminLead[]; pagination: any } | null>(null);
  const [page,      setPage]      = useState(1);
  const [days,      setDays]      = useState(30);
  const [eventType, setEventType] = useState('');
  const [loading,   setLoading]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ page: String(page), limit: '30', days: String(days) });
    if (eventType) q.set('eventType', eventType);
    apiFetch<typeof data>(`/admin/leads?${q}`, token)
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [token, page, days, eventType]);

  useEffect(() => { load(); }, [load]);

  const bizName = (lead: AdminLead) =>
    lead.businessId && typeof lead.businessId === 'object' ? lead.businessId.name : '—';

  const EVENT_COLORS: Record<string, string> = {
    booking_click: 'bg-violet-100 text-violet-700',
    map_click:     'bg-blue-100   text-blue-700',
    profile_view:  'bg-slate-100  text-slate-600',
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        <select value={days} onChange={e => { setDays(Number(e.target.value)); setPage(1); }} className={filterClass()}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <select value={eventType} onChange={e => { setEventType(e.target.value); setPage(1); }} className={filterClass()}>
          <option value="">All event types</option>
          <option value="profile_view">Profile View</option>
          <option value="map_click">Map Click</option>
          <option value="booking_click">Booking Click</option>
        </select>
        <span className="text-sm text-slate-400 font-medium ml-1">{data?.pagination?.total ?? 0} total events</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 overflow-hidden">
        {loading ? <Spinner /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>{['Time', 'Business', 'Event', 'Source'].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(data?.leads ?? []).map(l => (
                <tr key={l._id} className="hover:bg-slate-50/40 transition-colors">
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{format(new Date(l.timestamp), 'MMM d, HH:mm')}</td>
                  <td className="px-4 py-3 font-semibold text-slate-700">{bizName(l)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${EVENT_COLORS[l.eventType] ?? 'bg-slate-100 text-slate-600'}`}>
                      {l.eventType.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 capitalize text-xs font-medium">{l.source}</td>
                </tr>
              ))}
              {(data?.leads ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">No lead events yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
        <div className="px-4 pb-3"><Pagination page={page} pages={data?.pagination?.pages ?? 1} onChange={setPage} /></div>
      </div>
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────

// ─── Features tab ────────────────────────────────────────────────────────────

const FLAG_META: { key: string; label: string; description: string; group: string }[] = [
  // Public site
  { key: 'bookingPlatform',       label: 'Platform Booking',      group: 'Public site',         description: 'Show "Book Now" flow — activities, payment, confirmation pages' },
  { key: 'customAdventures',      label: 'Custom Adventures',     group: 'Public site',         description: 'Show custom adventure request pages and sections' },
  // Business dashboard
  { key: 'businessAnalytics',     label: 'Analytics Dashboard',   group: 'Business dashboard',  description: 'Old analytics tab with revenue/booking charts' },
  { key: 'businessBookings',      label: 'Booking Management',    group: 'Business dashboard',  description: 'Reservations list, status updates' },
  { key: 'businessCalendar',      label: 'Calendar',              group: 'Business dashboard',  description: 'Activity slots calendar view' },
  { key: 'businessCommunication', label: 'Communication',         group: 'Business dashboard',  description: 'AI chatbot and customer messaging' },
  { key: 'businessStaff',         label: 'Staff Management',      group: 'Business dashboard',  description: 'Team members and roles' },
  { key: 'businessMapping',       label: 'Mapping Tools',         group: 'Business dashboard',  description: 'Trail maps and points of interest' },
  { key: 'businessLivestock',     label: 'Wildlife Management',   group: 'Business dashboard',  description: 'Animal inventory and care tracking' },
  { key: 'businessFunding',       label: 'Funding',               group: 'Business dashboard',  description: 'Funding resources and financial tools' },
  // Always-on
  { key: 'partnerStats',          label: 'Partner Stats',         group: 'Always on',           description: 'Lead analytics dashboard — cannot be disabled here' },
];

function FeaturesTab({ token }: { token: string }) {
  const [flags,   setFlags]   = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState<string | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch(`${API}/features`)
      .then(r => r.json())
      .then(d => setFlags(d.flags ?? {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (key: string, current: boolean) => {
    setSaving(key); setMessage('');
    try {
      const res = await fetch(`${API}/admin/features`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: !current }),
      });
      const d = await res.json();
      if (res.ok) { setFlags(d.flags ?? {}); setMessage(`${key} → ${!current}`); }
      else setMessage(d.error ?? 'Update failed');
    } catch { setMessage('Network error'); }
    finally { setSaving(null); }
  };

  const groups = [...new Set(FLAG_META.map(f => f.group))];

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-sm text-amber-800">
        <p className="font-semibold mb-1">Feature flags control what's visible in the UI.</p>
        <p className="text-amber-700">Changes take effect the next time a user loads the page. Disabling a flag hides the UI — it does not disable the underlying API.</p>
      </div>

      {message && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-2 text-sm font-medium">{message}</div>}

      {loading ? <Spinner /> : groups.map(group => (
        <div key={group} className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 overflow-hidden">
          <div className="px-5 py-3 bg-slate-50/80 border-b border-slate-100">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{group}</h3>
          </div>
          <div className="divide-y divide-slate-50">
            {FLAG_META.filter(f => f.group === group).map(f => {
              const isOn   = flags[f.key] ?? false;
              const isLocked = f.group === 'Always on';
              return (
                <div key={f.key} className="flex items-center justify-between px-5 py-4">
                  <div className="flex-1 min-w-0 pr-4">
                    <p className="text-sm font-semibold text-slate-800">{f.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{f.description}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs font-semibold ${isOn ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {isOn ? 'ON' : 'OFF'}
                    </span>
                    <button
                      disabled={isLocked || saving === f.key}
                      onClick={() => toggle(f.key, isOn)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 ${isOn ? 'bg-emerald-500' : 'bg-slate-200'}`}
                      style={isOn && !isLocked ? { backgroundColor: PRIMARY } : {}}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isOn ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function LoginScreen({ onLogin }: { onLogin: (token: string) => void }) {
  const [email,    setEmail]    = useState('mariolallana12@gmail.com');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok || !data.token)    { setError(data.error || 'Login failed'); return; }
      if (decodeRole(data.token) !== 'admin') { setError('This account does not have admin access.'); return; }
      localStorage.setItem(STORE_KEY, data.token);
      onLogin(data.token);
    } catch { setError('Could not reach the server.'); }
    finally   { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a3d1e 0%, #2C5F2D 50%, #1a3d1e 100%)' }}>
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
        <div className="mb-6 text-center">
          <div className="w-14 h-14 rounded-xl mx-auto mb-3 flex items-center justify-center text-2xl shadow-lg"
            style={{ backgroundColor: PRIMARY }}>
            🌿
          </div>
          <h1 className="text-xl font-bold text-slate-800">Wildy Internal</h1>
          <p className="text-sm text-slate-400 mt-1">Founder dashboard — restricted access</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Email" required className={filterClass() + ' w-full'} />
          <input type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Password" required className={filterClass() + ' w-full'} />
          {error && <p className="text-sm text-red-600 font-medium">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full py-2.5 text-white rounded-xl font-semibold text-sm transition-opacity disabled:opacity-60 shadow-md"
            style={{ backgroundColor: PRIMARY }}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

const ACTION_META: Record<string, { label: string; className: string }> = {
  reply:        { label: 'Reply',           className: 'bg-amber-100 text-amber-800' },
  add_to_wildy: { label: 'Add to Wildy',    className: 'bg-brand-100 text-brand-700' },
  waiting:      { label: 'Waiting on them', className: 'bg-slate-100 text-slate-600' },
  none:         { label: 'No action',       className: 'bg-slate-100 text-slate-500' },
};

function InboxTab({ token }: { token: string }) {
  const [data,    setData]    = useState<InboxData | null>(null);
  const [status,  setStatus]  = useState<'open' | 'done'>('open');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch<InboxData>(`/admin/inbox-tasks?status=${status}`, token)
      .then(setData).catch(() => setError('Could not load tasks.')).finally(() => setLoading(false));
  }, [token, status]);

  useEffect(() => { load(); }, [load]);

  const setTaskStatus = async (id: string, next: 'open' | 'done') => {
    const res = await fetch(`${API}/admin/inbox-tasks/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next }),
    });
    if (res.ok) load(); else setError('Could not update the task.');
  };

  const openCount = (action: string) =>
    data?.counts.find(c => c._id.status === 'open' && c._id.action === action)?.count ?? 0;
  const summary = [
    { label: 'Need a reply',    value: openCount('reply') },
    { label: 'Add to Wildy',    value: openCount('add_to_wildy') },
    { label: 'Waiting on them', value: openCount('waiting') },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {summary.map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 p-4">
            <p className="text-2xl font-bold text-slate-800">{s.value}</p>
            <p className="text-sm text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <select value={status} onChange={e => setStatus(e.target.value as 'open' | 'done')} className={filterClass()}>
          <option value="open">Open tasks</option>
          <option value="done">Done</option>
        </select>
        <button onClick={load} className="px-3 py-2 text-sm font-semibold rounded-lg border border-primary text-primary hover:bg-primary/5 transition-colors">
          Refresh
        </button>
        <span className="text-sm text-slate-400 font-medium ml-1">
          {data?.lastSyncedAt ? `Inbox last synced ${format(new Date(data.lastSyncedAt), 'MMM d, HH:mm')}` : 'Inbox not synced yet'}
          {' · run /wildy-campaign-report for new replies'}
        </span>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5 overflow-x-auto">
        {loading && !data ? <Spinner /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>{['Received', 'From', 'Their reply', 'Next step', ''].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(data?.tasks ?? []).map(t => {
                const action = ACTION_META[t.action] ?? ACTION_META.none;
                return (
                  <tr key={t._id} className="hover:bg-slate-50/40 transition-colors align-top">
                    <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{format(new Date(t.lastInboundAt), 'MMM d')}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-700">{t.company}</p>
                      <p className="text-xs text-slate-400">{t.fromEmail}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-md">
                      <p className="line-clamp-2">{t.snippet || 'No text in the reply'}</p>
                      <p className="text-xs text-slate-400 capitalize mt-1">{t.sentiment}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${action.className}`}>{action.label}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <a href={`https://mail.google.com/mail/u/?authuser=wildyglobal@gmail.com#all/${t.threadId}`} target="_blank" rel="noopener noreferrer"
                        className="text-sm font-semibold text-primary hover:underline mr-3">Open email</a>
                      <button onClick={() => setTaskStatus(t._id, t.status === 'open' ? 'done' : 'open')}
                        className="text-sm font-semibold text-slate-500 hover:text-slate-800">
                        {t.status === 'open' ? 'Mark done' : 'Reopen'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {(data?.tasks ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400 text-sm">
                  {status === 'open' ? 'No open tasks.' : 'No finished tasks yet.'}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

function Dashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [tab, setTab] = useState<Tab>('overview');

  const tabs: { id: Tab; label: string; emoji: string }[] = [
    { id: 'overview',   label: 'Overview',   emoji: '📊' },
    { id: 'businesses', label: 'Businesses', emoji: '🏢' },
    { id: 'users',      label: 'Users',      emoji: '👥' },
    { id: 'leads',      label: 'Leads',      emoji: '🎯' },
    { id: 'inbox',      label: 'Inbox',      emoji: '📥' },
    { id: 'playbook',   label: 'Playbook',   emoji: '📘' },
    { id: 'features',   label: 'Features',   emoji: '🚩' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar — forest green matching public app primary */}
      <header className="sticky top-0 z-10 shadow-lg" style={{ backgroundColor: PRIMARY }}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🌿</span>
            <div>
              <span className="font-bold text-white text-base">Wildy Internal</span>
              <span className="text-emerald-300 text-xs ml-2 hidden sm:inline">Founder Dashboard</span>
            </div>
          </div>
          <button onClick={onLogout}
            className="text-sm text-emerald-200 hover:text-white transition-colors font-medium">
            Sign out
          </button>
        </div>
        {/* Tab bar */}
        <div className="max-w-7xl mx-auto px-6 flex gap-0.5 pb-0 overflow-x-auto [scrollbar-width:none]">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`shrink-0 whitespace-nowrap px-5 py-2.5 text-sm font-semibold rounded-t-lg transition-all ${
                tab === t.id
                  ? 'bg-slate-50 text-slate-800'
                  : 'text-emerald-200 hover:text-white hover:bg-white/10'}`}>
              <span className="mr-1.5">{t.emoji}</span>{t.label}
            </button>
          ))}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {tab === 'overview'   && <OverviewTab   token={token} />}
        {tab === 'businesses' && <BusinessesTab token={token} />}
        {tab === 'users'      && <UsersTab      token={token} />}
        {tab === 'leads'      && <LeadsTab      token={token} />}
        {tab === 'inbox'      && <InboxTab      token={token} />}
        {tab === 'playbook'   && <PlaybookTab />}
        {tab === 'features'   && <FeaturesTab   token={token} />}
      </main>
    </div>
  );
}

// ─── Page entry ───────────────────────────────────────────────────────────────

export function InternalToolPage() {
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem(STORE_KEY);
    if (!stored) return null;
    try {
      const payload = JSON.parse(atob(stored.split('.')[1]));
      if (payload.role !== 'admin') return null;
      if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    } catch { return null; }
    return stored;
  });

  const handleLogout = () => { localStorage.removeItem(STORE_KEY); setToken(null); };

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
}
